import { Clock, AlertTriangle } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

/**
 * Small pill badge that surfaces upcoming subscription due dates on the Hub.
 * Hidden when due-date is more than 14 days away. Shows amber for ≤14d,
 * red for ≤3d / grace / locked.
 */
const SubscriptionCountdownBadge = () => {
  const { nextDueDate, status, isLocked, ready } = useSubscription();

  if (!ready || !nextDueDate) return null;

  const due = new Date(nextDueDate);
  if (isNaN(due.getTime())) return null;

  const now = new Date();
  const ms = due.getTime() - now.getTime();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));

  // Hide if app is fully locked (lock screen handles that), and hide when >14d remaining
  if (isLocked) return null;
  if (days > 14) return null;

  const isOverdue = days < 0;
  const isUrgent = days <= 3 || status === "grace";

  const tone = isUrgent || isOverdue
    ? "bg-red-500/15 border-red-500/50 text-red-300"
    : "bg-amber-500/15 border-amber-500/50 text-amber-300";

  const label = isOverdue
    ? `Subscription overdue by ${-days} day${-days === 1 ? "" : "s"}`
    : days === 0
    ? "Subscription due today"
    : `Subscription renews in ${days} day${days === 1 ? "" : "s"}`;

  const Icon = isUrgent || isOverdue ? AlertTriangle : Clock;

  return (
    <div
      data-testid="subscription-countdown-badge"
      className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wider ${tone}`}
      title={`Next due date: ${due.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
  );
};

export default SubscriptionCountdownBadge;
